import { Component, Input, OnInit } from '@angular/core';
import { LoggerService } from '@app/shared/services/logger.service';
import { MatDialog } from '@angular/material/dialog';
import { MtxDrawer } from '@ng-matero/extensions/drawer';
import { SiteTour, SiteTourService } from '@app/shared/services/site-tours.service';
import {
  EditToursComponent
} from '@app/pages/authenticated/pages/site-tours/components/edit-tours/edit-tours.component';
import { PanelWidths } from '@app/shared/constants';
import { ModalConfirmationComponent } from '@app/shared/components/modal-confirmation/modal-confirmation.component';
import { environment } from '../../../../../../../environments/environment';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { AdminUser } from '@app/shared/services/user.service';

@Component({
  standalone: false,
  selector: 'app-list-site-tours',
  templateUrl: './list-site-tours.component.html',
})
export class ListSiteToursComponent implements OnInit {
  @Input() user: AdminUser;
  @Input() canEdit: boolean = false;

  public isLoading: boolean = true;
  public displayColumns: string[] = ['name', 'page', 'type', 'status', 'actions'];
  public tours: SiteTour[] = [];
  public CDN_URL: string = environment.CDN_URL;

  constructor(
    private logger: LoggerService,
    private dialog: MatDialog,
    private drawer: MtxDrawer,
    private siteTourService: SiteTourService,
    private bannerNotificationsService: BannerNotificationsService
  ) {}

  ngOnInit() {
    this.isLoading = true;
    if (this.user) {
      this.loadTours();
    }
  }

  public openTourDrawer(selectedSiteTour?: SiteTour): void {
    if (this.canEdit) {
      const drawerRef = this.drawer.open(EditToursComponent, {
        width: PanelWidths.desktopFull,
        disableClose: true,
        closeOnNavigation: false,
        data: { siteTour: selectedSiteTour },
      });
      drawerRef.afterDismissed().subscribe((result) => {
        if (result) {
          this.bannerNotificationsService.success('Site tour was saved');
          this.loadTours();
        }
      });
    }
  }

  public loadTours(): void {
    this.isLoading = true;
    this.siteTourService.getAllTours()
      .subscribe(
        (tours: SiteTour[]) => {
          this.tours = tours;
          this.isLoading = false;
          this.logger.log('GET site tours', tours);
        },
        (error) => {
          this.bannerNotificationsService.error(`Failed to load tours: ${error.message}`);
          this.logger.error('GET site tours error', error);
          this.isLoading = false;
        }
      );
  }

  public editTour(selectedSiteTour: SiteTour): void {
    this.openTourDrawer(selectedSiteTour);
  }

  public activateTour(tour: SiteTour): void {
    if (!tour.id) return;

    if (this.canEdit) {
      this.siteTourService.activateTour(tour.id)
        .subscribe(
          updatedTour => {
            tour.isActive = updatedTour.isActive;
          },
          error => {
            this.bannerNotificationsService.error(`Failed to activate tour: ${error.message}`);
            this.logger.error('UPDATE active site tours error', error);
          }
        );
    }
  }

  public deactivateTour(tour: SiteTour): void {
    if (!tour.id) return;

    if (this.canEdit) {
      this.siteTourService.deactivateTour(tour.id)
        .subscribe(
          updatedTour => {
            tour.isActive = updatedTour.isActive;
          },
          error => {
            this.bannerNotificationsService.error(`Failed to deactivate tour: ${error.message}`);
            this.logger.error('UPDATE deactive site tours error', error);
          }
        );
    }
  }

  public deleteTour(tour: SiteTour): void {
    if (!tour.id) return;
    if (this.canEdit) {
      const dialogRef = this.dialog.open(ModalConfirmationComponent, {
        data: {
          title: 'Delete Site Tour',
          content: `Are you sure you want to delete the tour "${tour.name}"?`,
          confirmBtnLabel: 'Delete',
          btnColor: 'warn',
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.siteTourService.deleteTour(tour.id)
            .subscribe(
              () => {
                // Remove from local array
                this.loadTours();
              },
              error => {
                this.bannerNotificationsService.error(`Failed to delete tour: ${error.message}`);
                this.logger.error('UPDATE delete site tour error', error);
              }
            );
        }
      });
    }
  }
}
